<div #tableContainer
  class="w-full rounded-lg border-b border-slate-400 bg-white relative"
  [style.overflow-y]="scrollMaxHeight ? 'auto' : 'visible'"
  [style.overflow-x]="scrollMaxHeight ? 'auto' : 'visible'"
  [style.max-height]="scrollMaxHeight || null"
  [style.-webkit-overflow-scrolling]="scrollMaxHeight ? 'touch' : null">
  <table class="w-full min-w-max border-collapse" appTableColumnResize [resizeMinColumnWidth]="72"
    [attr.data-column-reorder-active]="columnReorderDragInProgress ? 'true' : null"
    [class.table-bordered]="isBordered">
    <thead>
      <ng-container *ngIf="groupHeaderTemplate">
        <ng-container *ngTemplateOutlet="groupHeaderTemplate"></ng-container>
      </ng-container>
      <tr [ngClass]="getHeaderRowClass()">
        <th *ngIf="rowReorderable"
          class="sticky top-0 z-[32] bg-primary px-1 py-2 text-center text-[13px] border-r border-white/20 w-9 min-w-[2.25rem] rounded-tl-lg"
          [class.rounded-tl-lg]="!isSelectable"
          [ngClass]="headerFontWeightClass"
          title="Drag rows using the handle in each row">
          <i class="fas fa-bars text-white/90 text-sm leading-none" aria-hidden="true"></i>
        </th>
        <th *ngIf="isSelectable"
          class="sticky top-0 z-[32] bg-primary px-2 py-2 text-center text-[13px] border-r border-white/20 rounded-tl-lg"
          [class.rounded-tl-lg]="!rowReorderable"
          [ngClass]="headerFontWeightClass">
          <input type="checkbox" [checked]="isAllSelected()" [indeterminate]="isIndeterminate()"
            (change)="onSelectAll($event)" class="w-3 h-3 text-primary border-border rounded focus:ring-primary" />
        </th>
        <th *ngFor="let column of displayColumns; let last = last; let first = first" [class.cursor-pointer]="column.sortable && !columnReorderDragInProgress"
          [class.select-none]="column.sortable" (click)="!columnReorderDragInProgress && column.sortable ? onSort(column) : null"
          [style.text-align]="getResolvedColumnAlign(column)"
          [class.rounded-tr-lg]="last && !showActionsColumn && !isSelectable && !rowReorderable"
          [class.border-l-0]="first"
          [class.cursor-move]="columnReorderable"
          [class.opacity-60]="draggingColumnKey === column.key"
          [class.table-column-reorder-highlight-header]="draggingColumnKey === column.key"
          [attr.draggable]="columnReorderable ? true : null"
          (dragstart)="onColumnDragStart(column.key, $event)"
          (dragover)="onColumnDragOver(column.key, $event)"
          (drop)="onColumnDrop(column.key, $event)"
          (dragend)="onColumnDragEnd()"
          class="sticky top-0 z-[32] bg-primary px-2 py-2 text-left text-sm border-r border-l border-white/20 last:border-r-0 relative min-w-0 overflow-visible"
          [ngClass]="headerFontWeightClass">
          <div class="flex items-center gap-2 min-w-0 w-full">
            <ng-container *ngIf="column.headerTemplate; else defaultHeader">
              <ng-container
                *ngTemplateOutlet="column.headerTemplate; context: { $implicit: column, column: column }"></ng-container>
            </ng-container>
            <ng-template #defaultHeader>
              <span class="flex flex-1 min-w-0 items-center gap-1"
                [class.overflow-hidden]="!isHeaderLabelExpanded(column)"
                [class.overflow-visible]="isHeaderLabelExpanded(column)">
                <i *ngIf="column.headerIcon" class="text-inherit opacity-90 flex-shrink-0 {{ column.headerIcon }}" style="font-size: 0.875rem;"></i>
                <span
                  class="min-w-0"
                  [class.truncate]="!isHeaderLabelExpanded(column)"
                  [class.whitespace-nowrap]="!isHeaderLabelExpanded(column)"
                  [class.whitespace-normal]="isHeaderLabelExpanded(column)"
                  [attr.title]="column.label">
                  {{ column.label }}
                </span>
                <span *ngIf="column.sortable && getSortIcon(column)" class="text-white/80 flex-shrink-0">
                  {{ getSortIcon(column) }}
                </span>
              </span>
            </ng-template>

            <!-- Column filter dropdown (enabled by default; set filterable:false to hide) -->
            <span *ngIf="column.filterable !== false" class="column-filter-trigger relative inline-block ml-1 flex-shrink-0" (click)="$event.stopPropagation()">
              <button type="button" (click)="openColumnFilter(column.key, $event)"
                [class.ring-2]="columnFilterOpen === column.key"
                [ngClass]="isColumnFilterActive(column) ? 'bg-white/20 ring-2 ring-amber-300/80' : ''"
                class="relative p-0.5 rounded hover:bg-white/20 text-white/90 focus:outline-none"
                [title]="isColumnFilterActive(column) ? 'Filter active - click to change' : 'Filter column'">
                <i class="fas fa-chevron-down text-xs" [ngClass]="isColumnFilterActive(column) ? 'text-amber-200' : 'text-white/90'"></i>
                <span *ngIf="isColumnFilterActive(column)" class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-300"></span>
              </button>
            </span>
          </div>
        </th>
        <th *ngIf="showActionsColumn" [class.rounded-tr-lg]="!isSelectable"
          class="sticky top-0 z-[32] bg-primary px-2 py-2 text-[13px] text-white border-l border-white/20 border-r-0 whitespace-nowrap actions-column no-print overflow-visible"
          [class.text-center]="actionsColumnAlign !== 'right'"
          [class.text-right]="actionsColumnAlign === 'right'"
          [ngClass]="headerFontWeightClass">
          <div class="flex items-center gap-2" [class.justify-center]="actionsColumnAlign !== 'right'" [class.justify-end]="actionsColumnAlign === 'right'">
            <i *ngIf="actionsHeaderIcon" class="text-inherit opacity-90 {{ actionsHeaderIcon }}" style="font-size: 0.875rem;"></i>
            <span>Actions</span>
          </div>
        </th>
      </tr>
    </thead>
    <tbody>
      <!-- Virtualized top spacer -->
      <tr *ngIf="virtualEnabled">
        <td [attr.colspan]="bodyColSpan"
          [style.height.px]="topPadding"></td>
      </tr>

      <tr *ngFor="let row of renderRows; trackBy: trackByRow; let i = index; let last = last"
        [class]="getRowClasses(row, i, last)"
        [ngStyle]="getResolvedRowStyle(row)"
        [ngClass]="rowReorderRowClass(i)"
        [class.table-row-selected]="row?.__isSelected === true || (isSelectable && isRowSelected(row))"
        [class.cursor-pointer]="isHoverable" (click)="onRowClick(row, $event)"
        (dblclick)="onRowDblclick(row)" (contextmenu)="onRowRightClick($event, row)"
        (dragover)="onRowReorderDragOver(i, $event)"
        (drop)="onRowReorderDrop(i, $event)">
        <ng-container *ngIf="isFullWidthRow?.(row) && fullWidthRowTemplate; else standardRowCells">
          <td [attr.colspan]="bodyColSpan"
            class="px-0 py-0 border-r-0 border-l-0 border-slate-400 align-middle">
            <ng-container
              *ngTemplateOutlet="fullWidthRowTemplate; context: { $implicit: row, row: row, colspan: bodyColSpan }"></ng-container>
          </td>
        </ng-container>
        <ng-template #standardRowCells>
        <td *ngIf="rowReorderable"
          [class.rounded-bl-lg]="last"
          class="px-1 py-2 text-center border-r border-slate-400 cursor-grab active:cursor-grabbing select-none align-middle"
          draggable="true"
          (dragstart)="onRowReorderDragStart(i, $event)"
          (dragend)="onRowReorderDragEnd()"
          (click)="$event.stopPropagation()"
          title="Drag to reorder row">
          <i class="fas fa-bars text-base text-slate-500 hover:text-slate-800" aria-hidden="true"></i>
        </td>
        <td *ngIf="isSelectable" (click)="$event.stopPropagation()" [class.rounded-bl-lg]="last && !rowReorderable"
          class="px-2 py-2 text-center border-r border-slate-400">
          <input type="checkbox" [checked]="isRowSelected(row)" (change)="onSelectRow($event, row)"
            (click)="$event.stopPropagation()" class="w-3 h-3 text-primary border-border rounded focus:ring-primary" />
        </td>
        <td *ngFor="let column of displayColumns; let lastCol = last; let firstCol = first"
          [style.text-align]="getResolvedColumnAlign(column)"
          [style.background-color]="getCellBackgroundColor(row, column)" [style.color]="getCellTextColor(row, column)"
          [class.rounded-br-lg]="lastCol && last"
          [class.border-l-0]="firstCol"
          [class.table-column-reorder-highlight-body]="draggingColumnKey === column.key"
          [ngClass]="['px-2 py-2 text-[13px] border-r border-l border-slate-400 last:border-r-0 min-w-0 align-middle overflow-hidden text-ellipsis whitespace-nowrap', getCellTextColorClass(row, column), bodyFontWeightClass]"
          class="">
          <ng-container *ngIf="column.cellTemplate; else defaultCell">
            <ng-container
              *ngTemplateOutlet="column.cellTemplate; context: { $implicit: row, row: row, column: column }"></ng-container>
          </ng-container>
          <ng-template #defaultCell>
            <ng-container *ngIf="column.key === 'electronic'; else textCell">
              <input
                type="checkbox"
                class="w-3 h-3 text-primary border-border rounded focus:ring-primary"
                [checked]="getCellValue(row, column)"
                disabled />
            </ng-container>
            <ng-template #textCell>
              <span class="block w-full min-w-0 truncate" [ngClass]="getCellSpanExtraClass(row, column)"
                [attr.title]="getCellTooltip(row, column)">{{ getCellValue(row, column) }}</span>
            </ng-template>
          </ng-template>
        </td>
        <td *ngIf="showActionsColumn" (click)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()" [class.rounded-br-lg]="last"
          class="actions-column no-print border-l border-slate-400 border-r-0"
          [ngClass]="['px-2 py-2 text-[13px] whitespace-nowrap overflow-visible', getActionsColumnTextColorClass(row), bodyFontWeightClass]">
          <div class="flex items-center gap-2 flex-nowrap" [class.justify-center]="actionsColumnAlign !== 'right'" [class.justify-end]="actionsColumnAlign === 'right'">
            <!-- Individual View Button -->
            <button *ngIf="onViewDetails" (click)="onViewClick($event, row)" (dblclick)="$event.stopPropagation()"
              [ngClass]="row.__isSelected ? ['p-2', 'rounded', 'transition-colors', getSelectedStatusTextClass(row?.status)] : ['p-2', 'text-[var(--primary)]', 'hover:bg-[var(--primary)]', 'hover:text-white', 'rounded', 'transition-colors']"
              title="View Details">
              <i class="fas fa-eye text-sm"></i>
            </button>

            <!-- Individual History Button -->
            <button *ngIf="onHistory" (click)="onHistoryClick($event, row)" (dblclick)="$event.stopPropagation()"
              [ngClass]="row.__isSelected ? ['p-2', 'rounded', 'transition-colors', getSelectedStatusTextClass(row?.status)] : ['p-2', 'text-[var(--primary)]', 'hover:bg-[var(--primary)]', 'hover:text-white', 'rounded', 'transition-colors']"
              title="History">
              <i class="fas fa-history text-sm"></i>
            </button>

            <!-- Individual Delete Button (red) -->
            <button *ngIf="onDelete" (click)="onDeleteClick($event, row)" (dblclick)="$event.stopPropagation()"
              [ngClass]="row.__isSelected ? ['p-2', 'hover:bg-red-100', 'rounded', 'transition-colors', getSelectedStatusTextClass(row?.status)] : ['p-2', 'text-red-600', 'hover:bg-red-100', 'rounded', 'transition-colors']" title="Delete">
              <i class="fas fa-trash text-sm"></i>
            </button>

            <ng-container *ngIf="actionMenuItems">
              <!-- Use Kebab Menu -->
              <ng-container *ngIf="useKebabMenu; else buttonActions">
                <button (click)="onActionsMenuClick($event, row)"
                  [ngClass]="row.__isSelected ? ['p-2', 'rounded', 'transition-colors', getSelectedStatusTextClass(row?.status), 'hover:bg-[var(--primary)]', 'hover:text-white'] : ['p-2', 'text-[var(--primary)]', 'hover:bg-[var(--primary)]', 'hover:text-white', 'rounded', 'transition-colors']"
                  title="More Actions">
                  <i class="fas fa-ellipsis-v text-sm"></i>
                </button>
              </ng-container>

              <!-- Standard Button Actions -->
              <ng-template #buttonActions>
                <button *ngFor="let item of getActionMenuItems(row)" (click)="onContextMenuAction(item.action, row)"
                  class="p-2 rounded transition-colors"
                  [ngClass]="item.action === 'delete' ? 'text-red-600 hover:bg-red-100 hover:text-red-700' : 'text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white'"
                  [title]="item.label">
                  <i class="fas" [ngClass]="item.icon"></i>
                </button>
              </ng-template>
            </ng-container>
          </div>
        </td>
        </ng-template>
      </tr>
      <tr *ngIf="(virtualEnabled ? (visibleRows.length === 0 && (tableData || []).length === 0) : renderRows.length === 0)">
        <td [attr.colspan]="bodyColSpan"
          class="px-2 py-4 text-center text-muted text-[13px]">
          {{ displayEmptyMessage }}
        </td>
      </tr>

      <!-- Virtualized bottom spacer: always when virtual (tfoot does not replace off-screen row height). -->
      <tr *ngIf="virtualEnabled">
        <td [attr.colspan]="bodyColSpan"
          [style.height.px]="bottomPadding"></td>
      </tr>
    </tbody>
    <tfoot *ngIf="footerRow || footerTemplate">
      <ng-container *ngIf="footerTemplate">
        <ng-container *ngTemplateOutlet="footerTemplate; context: { $implicit: { columns: displayColumns, isSelectable: isSelectable, showActionsColumn: showActionsColumn } }"></ng-container>
      </ng-container>
      <tr *ngIf="footerRow && !footerTemplate" class="table-footer-row bg-slate-100" [class.table-footer-sticky]="stickyFooter" [ngClass]="bodyTextColorClass || 'text-slate-800'">
        <td *ngIf="rowReorderable" class="px-1 py-2 border-r border-slate-400 border-t border-slate-400 bg-slate-100"></td>
        <td *ngIf="isSelectable" class="px-2 py-2 text-center border-r border-slate-400 border-t border-slate-400 bg-slate-100"></td>
        <td *ngFor="let column of displayColumns; let firstCol = first; let lastCol = last"
          [style.text-align]="getFooterCellAlign(column)"
          class="px-2 py-2 text-[13px] border-r border-l border-t border-slate-400 last:border-r-0 min-w-0 align-middle overflow-hidden text-ellipsis whitespace-nowrap bg-slate-100"
          [class.border-l-0]="firstCol"
          [ngClass]="bodyFontWeightClass">
          <span class="block w-full min-w-0 truncate" [ngClass]="getFooterSpanExtraClass(column)"
            [attr.title]="footerCellTooltip(column)">{{ getFooterCellValue(column) }}</span>
        </td>
        <td *ngIf="showActionsColumn" class="px-2 py-2 border-l border-t border-slate-400 border-r-0 bg-slate-100"></td>
      </tr>
    </tfoot>
  </table>

  <!-- Context Menu -->
  <app-context-menu *ngIf="contextMenuOpen" [items]="getActionMenuItems(contextMenuOpen.row)" [x]="contextMenuOpen.x"
    [y]="contextMenuOpen.y" [rowData]="contextMenuOpen.row"
    (itemClick)="onContextMenuAction($event.action, $event.rowData)" (close)="onContextMenuClose()"></app-context-menu>

</div>

<!-- Column filter portal: appended to document.body so fixed positioning is not broken by CSS containment/transform on scroll parents. -->
<div *ngIf="columnFilterOpen && openColumnFilterColumn as openCol"
  #columnFilterPortal
  class="column-filter-dropdown min-w-[160px] max-h-[280px] overflow-auto bg-white border border-slate-300 rounded-lg shadow-lg py-2"
  [ngStyle]="columnFilterDropdownStyle"
  (click)="$event.stopPropagation()">
  <div class="max-h-[260px] overflow-y-auto">
    <label *ngFor="let val of openColumnFilterUniqueValues; trackBy: trackByFilterValue"
      class="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-[13px] text-slate-800">
      <input type="checkbox" [checked]="isColumnFilterValueSelected(openCol, val)"
        (change)="toggleColumnFilter(openCol, val, $event)" class="rounded border-slate-400 text-primary">
      <span class="truncate" [title]="val">{{ val }}</span>
    </label>
  </div>
</div>